import { supabase } from '@/lib/supabase';
import { resetRedirectUrl } from '@/lib/auth-redirect';
import type { TeamMember } from '@/lib/team';
import type { FirmRole } from '@/types/database';

/** The caller's firm, owner first, with each person's email (0034). */
export async function fetchTeam(): Promise<TeamMember[]> {
  const { data, error } = await supabase.rpc('firm_team');
  if (error) throw error;
  return (data as TeamMember[] | null) ?? [];
}

/**
 * Invite someone to the caller's firm. The invite-member Edge Function checks
 * the caller may (owner, free seat, new address), sends the email, and adds
 * them to the firm. Throws with a sentence fit to show the inviter.
 *
 * The emailed link returns them to the address the INVITER is using — the
 * same rule as password reset.
 */
export async function inviteMember(
  email: string,
  role: Exclude<FirmRole, 'owner'> = 'member',
  origin: string = location.origin,
): Promise<void> {
  const { error } = await supabase.functions.invoke('invite-member', {
    body: { email: email.trim(), role, redirectTo: resetRedirectUrl(origin) },
  });
  if (error) throw new Error(await functionErrorMessage(error));
}

/**
 * Make someone an admin, or a member again (0042).
 *
 * A plain RPC rather than an Edge Function: no service-role key is needed —
 * the membership row is written by set_firm_member_role, which checks the
 * caller itself. The owner's role is not settable through it at all.
 */
export async function setMemberRole(userId: string, role: Exclude<FirmRole, 'owner'>): Promise<void> {
  const { error } = await supabase.rpc('set_firm_member_role', { p_user: userId, p_role: role });
  if (error) throw error;
}

/**
 * The `{ error }` sentence an Edge Function sent back, if there is one.
 * supabase-js reports any non-2xx as a generic FunctionsHttpError and puts the
 * Response on `context`; without reading it, every refusal ("all 6 seats
 * used") would surface as "Edge Function returned a non-2xx status code".
 */
export async function functionErrorMessage(
  err: unknown, fallback = 'The invitation couldn’t be sent. Please try again.',
): Promise<string> {
  const ctx = (err as { context?: { json?: () => Promise<unknown> } } | null)?.context;
  if (!ctx || typeof ctx.json !== 'function') return fallback;
  try {
    const body = await ctx.json() as { error?: unknown } | null;
    return typeof body?.error === 'string' && body.error ? body.error : fallback;
  } catch {
    return fallback;
  }
}

/**
 * Remove someone from the caller's firm (owner or admin; 0035, 0042). Their work stays
 * with the firm. Resolves with whether their never-used login was deleted too.
 */
export async function removeMember(userId: string): Promise<{ loginDeleted: boolean }> {
  const { data, error } = await supabase.functions.invoke('team-member', {
    body: { action: 'remove', userId },
  });
  if (error) throw new Error(await functionErrorMessage(error, 'That didn’t work. Please try again.'));
  return { loginDeleted: (data as { loginDeleted?: boolean } | null)?.loginDeleted === true };
}

/** Send an invitation again, to someone who hasn't accepted yet (0035). */
export async function resendInvite(userId: string, origin: string = location.origin): Promise<void> {
  const { error } = await supabase.functions.invoke('team-member', {
    body: { action: 'resend', userId, redirectTo: resetRedirectUrl(origin) },
  });
  if (error) throw new Error(await functionErrorMessage(error, 'The invitation couldn’t be sent again. Please try again.'));
}
